import { Box, Hidden } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Set } from 'immutable';
import React, { useCallback, useEffect, useMemo } from 'react';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import CopyClipsButton from '@ui/Button/legacy/CopyClipsButton';
import { useCanReadCustomWorkspace } from 'abilities/workspaces';
import {
  ProjectAccessAction,
  ProjectAccessSubject,
} from '../../../server/abilities/ProjectAccessEnums';
import { ClipObject, instanceOfCompletedClip } from '../../context/clips';
import { Can } from '../../context/projectAccess/ProjectAccessContext';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import { CheckboxInput } from '../Inputs';
import SearchField from '../Inputs/SearchField';
import BulkClipDownloadButton from '../Projects/Download/BulkClipDownloadButton';
import StudioClipActionTooltip from '../StudioClip/Actions/StudioClipActionTooltip';
import useStudioClips from './hooks/useStudioClips';
import useClipsSearch from './hooks/useStudioClips/useClipsSearch';
import { Project } from '../../../server/graphql/__generated.types';
import LoadingIcon from '../LoadingIndicators/CircularLoader';
import useSRAnnouncer from '../../lib/useSRAnnouncer';

import Tooltip from '../Tooltip';
import useIsLegacyProjectViewOnly from '../Studio/legacy/useIsLegacyProjectViewOnly';

const useStyles = makeStyles()(theme => ({
  action: {
    padding: theme.spacing(1, 1.5),
    '&:not(:disabled)': {
      color: theme.palette.common.white,
    },
    '&:hover': {
      // override the hover state in favor of a specific focus color
      color: `${theme.palette.inputs.focus}!important`,
    },
  },
  search: {
    maxWidth: theme.spacing(30),
    // using hardcoded pixel value due to falling in between breakpoint registers
    [theme.breakpoints.down(1350)]: {
      maxWidth: theme.spacing(26),
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    lineHeight: 1,
  },
  bulkSelectTextActive: {
    fontWeight: 'bold',
  },
  download: {
    paddingLeft: theme.spacing(1.25),
    paddingRight: theme.spacing(2.25),
  },
  filesSelected: {
    fontSize: theme.typography.pxToRem(12),
  },
  tooltip: {
    position: 'relative',
    left: 38,
  },
}));

// TODO: add a ProjectContext and consume these props via useContext hook
const StudioClipUserListBatchActions = (props: {
  project: Project;
  searchDisabled: boolean;
  setDownloadError?: (downloadError: string) => void;
}) => {
  const { project, searchDisabled, setDownloadError } = props;
  const { classes } = useStyles();
  const [{ clips, selectedClipIds }, { setSelectedClipIds }] = useStudioClips(
    project.id
  );
  const { searchInput, searchError, setSearchInput, onBlur } = useClipsSearch();
  const [, promptsDispatch] = usePromptsContext();
  const { announce } = useSRAnnouncer();
  const hasWorkspaces = useCanReadCustomWorkspace();
  const isLegacyViewOnly = useIsLegacyProjectViewOnly(project.studioVersion);

  const clipsAreCombining = useMemo(
    () =>
      Boolean(
        clips.find(
          c =>
            c?.combinedFrom?.length &&
            !c.isComplete &&
            Boolean(!c?.failedWithError)
        )
      ),
    [clips]
  );

  enum ButtonTooltip {
    COMBINE = 'Combine',
    MOVE = 'Move',
    COPY = 'Copy',
    DOWNLOAD = 'Download',
    DELETE = 'Delete',
  }

  const buttonData = {
    combine: { tooltip: ButtonTooltip.COMBINE },
    move: { tooltip: ButtonTooltip.MOVE },
    copy: { tooltip: ButtonTooltip.COPY },
    download: { tooltip: ButtonTooltip.DOWNLOAD },
    delete: { tooltip: ButtonTooltip.DELETE },
  };

  const handleCombine = useCallback(() => {
    promptsDispatch({
      type: 'OpenCombineClipsPrompt',
      payload: {
        projectId: project.id,
        clipIds: selectedClipIds.toArray(),
      },
    });
  }, [project.id, promptsDispatch, selectedClipIds]);

  const handleMoveClips = useCallback(() => {
    const selectedClips = selectedClipIds.reduce<Array<ClipObject>>(
      (acc, curr) => {
        const selectedClip = clips.find(clip => clip.id === curr);
        if (selectedClip && instanceOfCompletedClip(selectedClip)) {
          return acc.concat([selectedClip]);
        }
        return acc;
      },
      []
    );
    promptsDispatch({
      type: 'OpenMoveClipsToProjectPrompt',
      payload: {
        clips: selectedClips,
        project,
        hasWorkspaces,
      },
    });
  }, [clips, promptsDispatch, selectedClipIds, project, hasWorkspaces]);

  const handleDeleteClips = useCallback(() => {
    promptsDispatch({
      type: 'OpenBatchDeleteClipsPrompt',
      payload: {
        clips: clips as ClipObject[],
        clipIds: selectedClipIds.toArray(),
      },
    });
  }, [clips, selectedClipIds, promptsDispatch]);

  const handleSelectToggle = useCallback(() => {
    if (selectedClipIds.count() > 0) {
      // Clear selection
      setSelectedClipIds(Set());
    } else {
      // Select all clips (from the current project!)
      setSelectedClipIds(
        Set(
          clips
            .filter(clip => clip.projectId === project.id)
            .filter(clip => !clip.failedWithError && !clip.isDeleted)
            .map(clip => clip.id)
        )
      );
    }
  }, [clips, project.id, selectedClipIds, setSelectedClipIds]);
  const selectedClipCount = selectedClipIds.count();
  const noClipsSelected = selectedClipCount === 0;
  const itemsSelectedText = `${selectedClipCount} Clip${
    selectedClipCount !== 1 ? 's' : ''
  } Selected`;

  useEffect(() => {
    if (selectedClipCount > 0) {
      announce(
        `${selectedClipCount} clip${
          selectedClipCount === 1 ? '' : 's'
        } selected`
      );
    } else {
      announce('No clips selected');
    }
  }, [announce, selectedClipCount]);

  return (
    <Box className={classes.container}>
      {/* Selected clips checkbox/toggle */}
      <Box
        display="flex"
        alignItems="center"
        flexGrow={1}
        component="label"
        htmlFor="batch-clips-select-toggle"
        sx={{ cursor: 'pointer' }}
      >
        <CheckboxInput
          id="batch-clips-select-toggle"
          checked={selectedClipCount > 0}
          indeterminate={selectedClipCount < clips.length}
          onChange={handleSelectToggle}
          aria-label="Select all clips toggle"
        />
        <Typography
          variant="pSm"
          color={selectedClipCount ? 'primary' : 'secondary'}
          className={
            selectedClipCount ? classes.bulkSelectTextActive : undefined
          }
        >
          {itemsSelectedText}
        </Typography>
      </Box>
      <Box>
        <Box display="flex" alignItems="center">
          <Box
            display="grid"
            alignItems="center"
            gridAutoFlow={'column'}
            mr={1.5}
          >
            {/* Combine clips action */}

            <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
              <Box hidden={selectedClipCount < 2}>
                <StudioClipActionTooltip
                  title={
                    // eslint-disable-next-line no-nested-ternary
                    isLegacyViewOnly
                      ? 'Combine is disabled'
                      : clipsAreCombining
                      ? ''
                      : buttonData.combine.tooltip
                  }
                  placement="top"
                >
                  <Button
                    variant="icon"
                    aria-label="combine selected clips"
                    onClick={handleCombine}
                    disabled={isLegacyViewOnly}
                    className={classes.action}
                    aria-haspopup={'dialog'}
                  >
                    {clipsAreCombining ? (
                      <LoadingIcon size={16} />
                    ) : (
                      <Icon type="combineClips" />
                    )}
                  </Button>
                </StudioClipActionTooltip>
              </Box>
            </Can>
            {/* Move clips action */}
            <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
              <Box hidden={noClipsSelected}>
                <StudioClipActionTooltip
                  title={
                    isLegacyViewOnly
                      ? 'Move is disabled'
                      : buttonData.move.tooltip
                  }
                  placement="top"
                >
                  <Button
                    variant="icon"
                    aria-label="move selected clips"
                    onClick={handleMoveClips}
                    disabled={isLegacyViewOnly}
                    className={classes.action}
                    aria-haspopup={'dialog'}
                  >
                    <Icon type="move" />
                  </Button>
                </StudioClipActionTooltip>
              </Box>
            </Can>
            {/* Copy clips action */}
            <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
              <Box hidden={noClipsSelected}>
                <StudioClipActionTooltip
                  title={
                    isLegacyViewOnly
                      ? 'Copy is disabled'
                      : buttonData.copy.tooltip
                  }
                  placement="top"
                >
                  <CopyClipsButton
                    disabled={isLegacyViewOnly}
                    project={project}
                    hideLabel
                    clipIds={selectedClipIds.toArray()}
                    className={classes.action}
                    aria-haspopup={'dialog'}
                  />
                </StudioClipActionTooltip>
              </Box>
            </Can>
            {/* Download clips action (hidden on mobile mainly due to poor browser support) */}
            <Hidden smDown>
              <Box hidden={noClipsSelected}>
                <Can
                  I={ProjectAccessAction.Download}
                  a={ProjectAccessSubject.Project}
                >
                  <Box>
                    <StudioClipActionTooltip
                      title={buttonData.download.tooltip}
                      placement="top"
                    >
                      <BulkClipDownloadButton
                        className={classes.action}
                        clipIds={selectedClipIds.toArray()}
                        hideLabel
                        setDownloadError={setDownloadError}
                        audioType="clips"
                        projectId={project.id}
                        studioVersion={project.studioVersion}
                        title="Download clips"
                      />
                    </StudioClipActionTooltip>
                  </Box>
                </Can>
              </Box>
            </Hidden>
            {/* Delete clips action */}
            <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
              <Box hidden={noClipsSelected}>
                <StudioClipActionTooltip
                  title={buttonData.delete.tooltip}
                  placement="top"
                >
                  <Button
                    variant="icon"
                    aria-label="delete selected clips"
                    onClick={handleDeleteClips}
                    className={classes.action}
                    aria-haspopup={'dialog'}
                  >
                    <Icon type="delete" />
                  </Button>
                </StudioClipActionTooltip>
              </Box>
            </Can>
          </Box>
          <Box>
            <Tooltip
              text={
                searchDisabled
                  ? 'Search is not available for this language.'
                  : ''
              }
              classes={{ tooltip: classes.tooltip }}
              disabled={searchDisabled === false}
            >
              <Box>
                <SearchField
                  id="project-detail-search"
                  aria-label="Search for clips"
                  value={searchInput || ''}
                  onChange={event => setSearchInput(event.target.value)}
                  onClear={() => setSearchInput('')}
                  onBlur={() => onBlur({ projectId: project.id })}
                  className={classes.search}
                  disabled={searchDisabled}
                />
              </Box>
            </Tooltip>
            {searchError && (
              <Box position="absolute">
                <Typography role="alert" color="error" variant="pSm">
                  {searchError}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StudioClipUserListBatchActions;
